import RadarFicha from "@/pages/RadarFicha";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer, Polygon, Marker, useMap } from "react-leaflet";
import L from "leaflet";
import MarkerClusterGroup from "@/components/MarkerClusterGroup";
import ChipBar, { useSegmentChip, segmentCounts, filterBySegment } from "@/components/SegmentChipBar";
import ZonaDialog from "@/components/ZonaDialog";
import api from "@/lib/api";
import { Plus, RefreshCw, MapPin, Loader2, Search, X, Sparkles, AlertCircle, Pencil, Trash2, LayoutList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const CORDOBA_CENTER = [-31.42, -64.18];

// ---------------------------------------------------------------
// Custom pin builder — L.divIcon showing price + optional discount
// ---------------------------------------------------------------
// Coherencia PIN ↔ FICHA (regla de honestidad del scoring):
// El pin AFIRMA una oportunidad SOLO si `mediana_source === "barrio"`. Cuando
// la mediana viene de localidad o provincia (contexto), NO usamos los colores
// asertivos (verde/ámbar/rojo) — cae a "neutral" (gris). Es equivalente al
// estilo tenue del hero de la ficha (ver RadarFicha.jsx).
//
// - es_subvaluado ya es "barrio-only" por diseño (scoring.py cascade) → top/subv OK.
// - danger/warn miraban ratio_vs_mediana solo. Bug: un ctx-localidad con
//   ratio=0.30 salía ROJO en el pin pero GRIS en la ficha. Ahora coincide.
const isBarrioRef = (o) => o.mediana_source === "barrio";

const pinVariant = (o) => {
  if (o.es_subvaluado && o.es_dueno_directo) return "top";
  if (o.es_subvaluado) return "subv";
  if (isBarrioRef(o) && o.ratio_vs_mediana != null && o.ratio_vs_mediana < 0.35) return "danger";
  if (isBarrioRef(o) && o.ratio_vs_mediana != null && o.ratio_vs_mediana < 0.55) return "warn";
  return "neutral";
};

const formatPriceShort = (val, moneda) => {
  const n = Number(val) || 0;
  const sym = moneda === "USD" ? "USD" : (moneda || "$");
  if (n >= 1_000_000) return `${sym} ${(n / 1_000_000).toFixed(n >= 10_000_000 ? 1 : 2)}M`.replace(".0M", "M");
  if (n >= 1_000)     return `${sym} ${Math.round(n / 1_000)}k`;
  return `${sym} ${n.toLocaleString("es-AR")}`;
};

const discountPct = (o) => {
  // Coherencia con la ficha: solo mostramos el % encima del pin cuando la
  // mediana viene del BARRIO (afirmación de oportunidad). Para localidad /
  // provincia el ratio es CONTEXTO — la ficha lo muestra gris tenue, el pin
  // simplemente no lo pinta (el user lo ve al abrir).
  if (!isBarrioRef(o)) return null;
  if (o.ratio_vs_mediana == null || o.ratio_vs_mediana >= 1) return null;
  return Math.round((1 - o.ratio_vs_mediana) * 100);
};

const buildPinIcon = (o, isHero = false) => {
  const variant = pinVariant(o);
  const price = formatPriceShort(o.precio, o.moneda);
  const disc = discountPct(o);
  const heroCls = isHero ? " radar-pin--hero" : "";
  const html = `
    <div class="radar-pin radar-pin--${variant}${heroCls}" ${isHero ? 'title="Top oportunidad del día"' : ""}>
      <span>${price}</span>
      ${disc != null && disc > 0 ? `<span class="radar-pin__discount">-${disc}%</span>` : ""}
    </div>`;
  return L.divIcon({
    html,
    className: "radar-pin-wrap",
    iconSize: [null, null],
    iconAnchor: [0, 0],
  });
};

const escapeHtml = (s = "") => String(s)
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/"/g, "&quot;").replace(/'/g, "&#39;");

const buildPopupHtml = (o) => {
  const cover = o.fotos?.[0];
  const price = `${o.moneda} ${Number(o.precio).toLocaleString("es-AR")}`;
  const disc = discountPct(o);
  const badges = [];
  if (o.es_subvaluado) badges.push(`<span style="background:#d1fae5;color:#065f46;border:1px solid #a7f3d0;padding:1px 6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">Subvaluada</span>`);
  if (o.es_dueno_directo) badges.push(`<span style="background:#a7f3d0;color:#064e3b;border:1px solid #6ee7b7;padding:1px 6px;font-size:10px;font-weight:700;text-transform:uppercase;letter-spacing:0.06em;">Dueño directo</span>`);
  return `
    <div style="font-family:Manrope,sans-serif;overflow:hidden;">
      ${cover ? `<div style="width:100%;height:120px;overflow:hidden;background:#f1f5f9;">
        <img src="${escapeHtml(cover)}" style="width:100%;height:100%;object-fit:cover;display:block;" onerror="this.style.display='none'"/>
      </div>` : ""}
      <div style="padding:10px 12px 12px;">
        <div style="display:flex;flex-wrap:wrap;gap:4px;margin-bottom:6px;">${badges.join("")}</div>
        <div style="font-family:Outfit,sans-serif;font-weight:800;font-size:15px;color:#0f172a;line-height:1.1;">
          ${escapeHtml(price)}
          ${disc != null && disc > 0 ? `<span style="color:#059669;font-size:12px;font-weight:700;margin-left:4px;">-${disc}% vs mediana</span>` : ""}
        </div>
        <div style="font-size:11px;color:#64748b;margin-top:4px;">
          ${escapeHtml(o.superficie_m2 || "?")} m² · USD/m² ${Math.round(o.precio_m2 || 0)}
        </div>
        ${o.ref_label ? `<div style="font-size:10px;color:#475569;margin-top:6px;padding:4px 6px;background:#f1f5f9;border-left:2px solid #10b981;line-height:1.3;">${escapeHtml(o.ref_label)}${o.ratio_vs_mediana != null ? ` <strong style="color:${o.ratio_vs_mediana < 1 ? "#047857" : "#92400e"};">· ${o.ratio_vs_mediana < 1 ? "-" : "+"}${Math.abs(Math.round((1 - o.ratio_vs_mediana) * 100))}%</strong>` : ""}</div>` : ""}
        <div style="font-size:11px;color:#475569;margin-top:6px;line-height:1.35;">
          ${escapeHtml((o.titulo || "").slice(0, 80))}${(o.titulo || "").length > 80 ? "…" : ""}
        </div>
        ${o.motivos?.length ? `<div style="font-size:10px;color:#047857;margin-top:6px;font-weight:600;">✓ ${escapeHtml(o.motivos[0])}</div>` : ""}
        <button data-open-ficha="${escapeHtml(o.opportunity_id)}"
                data-testid="popup-open-${escapeHtml(o.opportunity_id)}"
                style="margin-top:10px;width:100%;padding:6px 10px;background:#047857;color:#fff;border:none;font-family:Outfit,sans-serif;font-weight:700;font-size:11px;letter-spacing:0.05em;text-transform:uppercase;cursor:pointer;">
          Abrir ficha →
        </button>
      </div>
    </div>`;
};

// Deterministic jitter (so pins don't stack at same coords)
const jitter = (lat, lng, seed) => {
  if (!lat || !lng) return [null, null];
  const s = String(seed).split("").reduce((a, c) => a + c.charCodeAt(0), 0);
  const dLat = (((s * 9301 + 49297) % 233280) / 233280 - 0.5) * 0.006;
  const dLng = (((s * 4919 + 12345) % 233280) / 233280 - 0.5) * 0.006;
  return [lat + dLat, lng + dLng];
};

const RecenterOnZone = ({ center }) => {
  const map = useMap();
  useEffect(() => {
    if (center && center[0] && center[1]) map.setView(center, 14);
  }, [center, map]);
  return null;
};

// Vuela al aviso seleccionado (deep-link o click en pin).
// Al abrir el panel lateral, Leaflet reajusta con invalidateSize.
const FlyToOpp = ({ opp, panelOpen }) => {
  const map = useMap();
  useEffect(() => {
    if (opp?.lat && opp?.lng) map.flyTo([opp.lat, opp.lng], Math.max(map.getZoom(), 16), { duration: 0.8 });
  }, [opp, map]);
  useEffect(() => {
    // El aside cambia el ancho del contenedor → Leaflet necesita re-medir.
    const t = setTimeout(() => map.invalidateSize(), 250);
    return () => clearTimeout(t);
  }, [panelOpen, map]);
  return null;
};

export default function RadarMapa() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [chip, setChip] = useSegmentChip("oport");
  const [zonas, setZonas] = useState([]);
  const [zonaSel, setZonaSel] = useState(null);
  // Panel lateral del aviso (Sprint P-2 · Unificación con Tierra).
  // Se abre por click en pin o por URL param ?opp=X. Mantiene la ruta
  // /radar/:id vía redirect (ver RadarFicha.jsx wrapper).
  const [selectedOppId, setSelectedOppId] = useState(searchParams.get("opp") || null);
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openNewZona, setOpenNewZona] = useState(false);
  const [editingZona, setEditingZona] = useState(null);
  const [deletingZona, setDeletingZona] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [previewPolygon, setPreviewPolygon] = useState(null);
  const [previewPin, setPreviewPin] = useState(null); // {lat, lng, draggable, onDrag}
  const [searching, setSearching] = useState(false);
  const [searchProgress, setSearchProgress] = useState(null);
  const [activeCapturaId, setActiveCapturaId] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [searchSummary, setSearchSummary] = useState(null);
  const [searchError, setSearchError] = useState(null);

  // Fetch aislable de opps por zona — para poder refrescarlo bajo demanda
  // (post-captura, post-cancel) sin depender del effect que reacciona a
  // zonaSel.zona_id (que NO re-corre si la zona seleccionada es la misma).
  //
  // Guarda "última llamada gana" (latest-wins): un contador monotónico
  // desestima resultados de fetches previos si el usuario cambió de zona
  // rápido antes de que respondan. Sin esto, dos requests concurrentes
  // podían pisar el state con la respuesta del que llegara segundo,
  // aunque fuera de la zona anterior (bug clásico de race en React).
  const oppsReqIdRef = useRef(0);
  const reloadOpps = React.useCallback(async () => {
    const zonaId = zonaSel?.zona_id;
    const myReqId = ++oppsReqIdRef.current;
    if (!zonaId) {
      // Cambio a "sin zona" — solo aplicamos si sigue siendo la última
      // intención del user.
      if (oppsReqIdRef.current === myReqId) setOpps([]);
      return;
    }
    try {
      const r = await api.get("/opportunities", {
        params: { zona_id: zonaId, limit: 2000 },
      });
      if (oppsReqIdRef.current !== myReqId) return;  // llegó tarde → descarto
      setOpps(r.data.items || []);
    } catch (e) {
      if (oppsReqIdRef.current !== myReqId) return;
      toast.error("No pude cargar los avisos de la zona");
    }
  }, [zonaSel?.zona_id]);

  // Abrir la ficha lateral + actualizar URL. Encapsula la mecánica del panel.
  const openAviso = React.useCallback((oid) => {
    if (!oid) return;
    setSelectedOppId(oid);
    const p = new URLSearchParams(searchParams);
    p.set("opp", oid);
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  const closeAviso = React.useCallback(() => {
    setSelectedOppId(null);
    const p = new URLSearchParams(searchParams);
    p.delete("opp");
    setSearchParams(p, { replace: true });
  }, [searchParams, setSearchParams]);

  // Traer datos del aviso seleccionado para poder centrar el mapa (flyTo).
  const [selectedOppLite, setSelectedOppLite] = useState(null);
  useEffect(() => {
    if (!selectedOppId) { setSelectedOppLite(null); return; }
    // Buscamos primero en `opps` (evitamos un fetch si ya lo tenemos cargado).
    const local = opps.find(o => o.opportunity_id === selectedOppId);
    if (local) { setSelectedOppLite(local); return; }
    api.get(`/opportunities/${selectedOppId}`).then(r => setSelectedOppLite(r.data)).catch(() => setSelectedOppLite(null));
  }, [selectedOppId, opps]);

  const load = async () => {    setLoading(true);
    try {
      const z = await api.get("/zonas");
      setZonas(z.data.items);
      if (!zonaSel && z.data.items.length) setZonaSel(z.data.items[0]);
      // Los opps se cargan en el effect que reacciona a zonaSel (filtro
      // server-side por zona_id). Ver useEffect debajo.
    } catch (e) {
      toast.error("Error cargando datos");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // ── Filtro server-side por zona_id (fix 2026-02-16) ────────────────
  // Antes: /opportunities?limit=200 + filter en cliente `o.zona_id === X`.
  // Con 517+ avisos el filter cliente perdía la mayoría porque el limit
  // cortaba a 200 (Villa Allende mostraba 6 en vez de 303).
  // Ahora el backend filtra por `zona_id` y devuelve `total` real. Le
  // pedimos un límite alto (2000) porque el mapa renderiza clusters — si
  // en el futuro una zona pasa 2000 avisos, migramos a `$geoIntersects`
  // + paginación cursor (anotado en backlog "pre-capital").
  //
  // Delegamos el fetch en `reloadOpps` para poder invocarlo también
  // post-captura (bug 2026-02-17: contadores stale hasta refresh manual).
  // El guard "latest-wins" adentro de reloadOpps reemplaza al `cancelled`
  // local que teníamos antes: si el user cambia de zona rápido, la
  // respuesta del fetch viejo se descarta.
  useEffect(() => { reloadOpps(); }, [reloadOpps]);

  const [showCostPreview, setShowCostPreview] = useState(false);

  // Preview honesto del costo — techo, no promesa. Basado en las fuentes
  // activas × maxItems default × $/item del actor. Botón cancelar durante la
  // corrida corta el gasto real igual.
  //   Properati: $0.0015/item · default maxItems 100 → tope $0.15/fuente.
  //   Zonaprop: apagado desde 15/07 (data basura).
  const costPreview = useMemo(() => {
    const runs = [{ name: "Properati", maxItems: 100, perItem: 0.0015 }];
    const total = runs.reduce((s, r) => s + r.maxItems * r.perItem, 0);
    return { runs, total };
  }, []);

  const runSearch = async () => {
    if (!zonaSel || searching) return;
    setSearching(true);
    setSearchError(null);
    setSearchSummary(null);
    setSearchProgress({ step: "queued", message: "Encolando búsqueda…" });
    const started = Date.now();
    try {
      // 1) Dispara la captura en background y recibe captura_id al instante.
      const res = await api.post("/opportunities/captura", {
        zona_id: zonaSel.zona_id,
      });
      const capturaId = res.data?.captura_id;
      if (!capturaId) throw new Error("El servidor no devolvió captura_id");
      setActiveCapturaId(capturaId);

      // 2) Polling cada 2 s. Timeout de seguridad: 10 min.
      const POLL_MS = 2000;
      const MAX_MS = 10 * 60 * 1000;
      let last = null;
      while (Date.now() - started < MAX_MS) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        try {
          // Timeout defensivo por request: si el GET se cuelga (Cloudflare,
          // red temblorosa) NO nos quedamos esperando indefinido. 10 s
          // alcanza — el endpoint es un find_one contra Mongo.
          const st = await api.get(`/opportunities/captura/${capturaId}`, { timeout: 10000 });
          last = st.data;
          setSearchProgress(last.progreso || null);
          if (last.status === "completada") break;
        } catch (pollErr) {
          // Sólo toleramos errores REALES de RED (timeout de axios,
          // network error). Cualquier otra cosa relanza y aborta el loop.
          const isNetTimeout = pollErr?.code === "ECONNABORTED" || pollErr?.code === "ERR_NETWORK";
          if (!isNetTimeout) throw pollErr;
          // Con errores de red seguimos polleando — el estado real vive en
          // Mongo, el próximo GET lo trae. Nunca vamos a quedar en un loop
          // "eterno" porque el MAX_MS de arriba corta.
        }
        // Estados terminales fuera del try/catch para que su throw no lo
        // capture accidentalmente (bug 2026-07-16: el catch se comía el
        // "status===error" y el loop no salía).
        if (last?.status === "cancelada") {
          const cost = last.resultado?.costo_total_usd ?? 0;
          throw new Error(`Búsqueda cancelada · costo hasta abort: $${Number(cost).toFixed(4)}`);
        }
        if (last?.status === "error") {
          throw new Error(last.error || "La búsqueda falló");
        }
      }
      if (!last || last.status === "en_curso") {
        throw new Error("La búsqueda tardó más de lo esperado. Volvé a intentar en unos minutos.");
      }
      const s = last.resultado || {};
      setSearchProgress(null);
      setSearchSummary({
        nuevas: s.persistidos_nuevos ?? 0,
        actualizadas: s.actualizados ?? 0,
        subvaluadas: s.subvaluados ?? 0,
        dueno_directo: s.dueno_directo ?? 0,
        filtradas_zona: s.filtrados_por_zona ?? 0,
        coords_actor: s.coords_desde_actor ?? 0,
        geocoded: s.geocodificados ?? 0,
        duracion_seg: Math.round((Date.now() - started) / 1000),
      });
      toast.success(
        `${s.persistidos_nuevos ?? 0} oportunidades nuevas · ${s.subvaluados ?? 0} subvaluadas · ${s.dueno_directo ?? 0} dueño directo`
      );
      // Refrescamos opps sí o sí — la zona no cambió, así que el effect
      // que reacciona a zonaSel.zona_id NO re-corre y los contadores
      // KPI (OPORTUNIDADES / TOP / VER TODO / "totales en la zona")
      // quedaban stale hasta refrescar a mano (bug 2026-02-17). El guard
      // latest-wins de reloadOpps protege contra race si el user hace
      // click en otra zona mientras se resuelve.
      await Promise.all([load(), reloadOpps()]);
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === "string"
        ? d
        : d?.message || d?.detail || e?.message || "No pudimos completar la búsqueda.";
      setSearchProgress(null);
      setSearchError(msg);
      toast.error(msg);
    } finally {
      setSearching(false);
      setActiveCapturaId(null);
      setCancelling(false);
    }
  };

  const cancelSearch = async () => {
    if (!activeCapturaId || cancelling) return;
    if (!window.confirm("¿Cancelar la búsqueda? Se aborta el/los run(s) de Apify (se deja de cobrar) y se registra el costo hasta el abort.")) return;
    setCancelling(true);
    try {
      const res = await api.post(`/opportunities/captura/${activeCapturaId}/cancel`);
      const cost = res.data?.costo_hasta_abort_usd ?? 0;
      const aborted = (res.data?.runs_aborted || []).length;
      toast.success(`Búsqueda cancelada · ${aborted} run(s) Apify abortados · costo $${Number(cost).toFixed(4)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "No pudimos cancelar");
      setCancelling(false);
    }
    // El loop de polling detecta status='cancelada' y sale por el catch → limpia todo.
  };

  // Opps of the selected zone with lat/lng — used for cluster building & counts.
  // NOTA: `opps` ya viene filtrada server-side por `zonaSel.zona_id` (ver
  // useEffect arriba). Sólo filtramos por lat/lng para pintar en el mapa.
  const zonaOppsAll = useMemo(() =>
    opps.filter(o => o.lat && o.lng),
  [opps]);

  // Counts for the chip bar
  const counts = useMemo(() => segmentCounts(zonaOppsAll), [zonaOppsAll]);

  // Apply chip filter to the current view
  const zonaOpps = useMemo(() => filterBySegment(zonaOppsAll, chip.id), [zonaOppsAll, chip]);

  // Hero pins: top 3 opps by score across the account (or the current chip subset if it's
  // "oport" / "top") — highlight only within the currently visible set.
  const heroIds = useMemo(() => {
    const eligible = zonaOpps
      .filter(o => o.es_subvaluado && (o.score || 0) > 0)
      .slice()
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .slice(0, 3)
      .map(o => o.opportunity_id);
    return new Set(eligible);
  }, [zonaOpps]);

  // Cluster markers — memoized to avoid rebuilding icons on every render.
  // Hero pins are excluded so they never cluster and stay visible (pulse-visible even at low zoom).
  const clusterMarkers = useMemo(() => zonaOpps
    .filter((o) => !heroIds.has(o.opportunity_id))
    .map((o) => {
    const [lat, lng] = jitter(o.lat, o.lng, o.opportunity_id);
    return {
      position: [lat, lng],
      icon: buildPinIcon(o, false),
      popupHtml: buildPopupHtml(o),
      onPopupOpen: (e) => {
        const el = e.popup.getElement();
        if (!el) return;
        const btn = el.querySelector("[data-open-ficha]");
        if (btn && !btn.dataset.wired) {
          btn.dataset.wired = "1";
          btn.addEventListener("click", () => {
            const oid = btn.getAttribute("data-open-ficha");
            if (oid) openAviso(oid);
          });
        }
      },
    };
  }), [zonaOpps, heroIds, openAviso]);

  // Hero pins — rendered as standalone Markers on top of clusters (always visible)
  const heroMarkers = useMemo(() => zonaOpps
    .filter((o) => heroIds.has(o.opportunity_id))
    .map((o) => {
      const [lat, lng] = jitter(o.lat, o.lng, o.opportunity_id);
      return { o, position: [lat, lng], icon: buildPinIcon(o, true) };
  }), [zonaOpps, heroIds]);

  // Convert GeoJSON Polygon/MultiPolygon to Leaflet lat-lng arrays for <Polygon />
  const toLeafletPolygon = (geo) => {
    if (!geo?.coordinates) return null;
    if (geo.type === "Polygon") return geo.coordinates[0].map(([lng, lat]) => [lat, lng]);
    if (geo.type === "MultiPolygon") return geo.coordinates.map(poly => poly[0].map(([lng, lat]) => [lat, lng]));
    return null;
  };
  const zonaPolygon = useMemo(() => toLeafletPolygon(zonaSel?.poligono), [zonaSel]);
  const previewLeaflet = useMemo(() => toLeafletPolygon(previewPolygon), [previewPolygon]);

  // Compute a center: preview polygon > selected zona > first opp > default
  const centerFromPoly = (leafPoly) => {
    if (!leafPoly) return null;
    // Handle both Polygon (array of [lat,lng]) and MultiPolygon (array of rings)
    const flat = Array.isArray(leafPoly[0][0]) ? leafPoly.flat() : leafPoly;
    if (!flat.length) return null;
    const avgLat = flat.reduce((s, p) => s + p[0], 0) / flat.length;
    const avgLng = flat.reduce((s, p) => s + p[1], 0) / flat.length;
    return [avgLat, avgLng];
  };
  const center = centerFromPoly(previewLeaflet) || centerFromPoly(zonaPolygon)
    || (zonaOpps[0]?.lat ? [zonaOpps[0].lat, zonaOpps[0].lng] : CORDOBA_CENTER);

  const handleDelete = async () => {
    if (!deletingZona) return;
    setDeleting(true);
    try {
      await api.delete(`/zonas/${deletingZona.zona_id}`);
      toast.success(`Zona "${deletingZona.nombre}" eliminada`);
      const removedId = deletingZona.zona_id;
      setDeletingZona(null);
      // Si borramos la seleccionada, elegir otra en el próximo load
      if (zonaSel?.zona_id === removedId) setZonaSel(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar zona");
    } finally { setDeleting(false); }
  };

  return (
    <div className="p-8 lg:p-12">
      <header className="mb-8 flex items-end justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700 mb-3">Mapa</p>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Oportunidades geolocalizadas
          </h1>
          <p className="font-body text-base text-slate-600 mt-2">
            <span className="font-bold text-slate-900">{zonaOpps.length}</span>{" "}
            <span className="text-emerald-800">{chip.label.toLowerCase()}</span>
            {" en "}{zonaSel?.nombre || "todas las zonas"} — {zonaOppsAll.length} totales{zonaSel ? " en la zona" : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={zonaSel?.zona_id || ""} onValueChange={(v) => setZonaSel(zonas.find(z => z.zona_id === v))}>
            <SelectTrigger data-testid="zona-selector" className="w-[220px] rounded-none border-slate-300">
              <SelectValue placeholder="Seleccioná una zona" />
            </SelectTrigger>
            <SelectContent>
              {zonas.map(z => (
                <SelectItem key={z.zona_id} value={z.zona_id}>
                  {z.nombre} <span className="text-xs opacity-60">· {z.tipo}</span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button data-testid="new-zona-btn" onClick={() => setOpenNewZona(true)} variant="outline" className="rounded-none border-slate-300">
            <Plus className="w-4 h-4 mr-2" /> Nueva zona
          </Button>
          <Button
            data-testid="edit-zona-btn"
            onClick={() => zonaSel && setEditingZona(zonaSel)}
            disabled={!zonaSel}
            variant="outline"
            className="rounded-none border-slate-300"
            title={zonaSel ? `Editar "${zonaSel.nombre}"` : "Elegí una zona"}
          >
            <Pencil className="w-4 h-4" />
          </Button>
          <Button
            data-testid="delete-zona-btn"
            onClick={() => zonaSel && setDeletingZona(zonaSel)}
            disabled={!zonaSel}
            variant="outline"
            className="rounded-none border-slate-300 text-red-700 hover:bg-red-50 hover:text-red-800 disabled:text-slate-400"
            title={zonaSel ? `Borrar "${zonaSel.nombre}"` : "Elegí una zona"}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
          <Button
            data-testid="ver-tablero-btn"
            onClick={() => navigate(`/radar/tablero${zonaSel ? `?zona=${zonaSel.zona_id}` : ""}`)}
            variant="outline"
            className="rounded-none border-slate-300"
            title={zonaSel ? `Ver en tablero: ${zonaSel.nombre}` : "Ver todos en tablero"}
          >
            <LayoutList className="w-4 h-4 mr-2" /> Ver en tablero
          </Button>
          <Button onClick={load} variant="outline" className="rounded-none border-slate-300">
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Legend */}
      <div className="bg-white border border-slate-200 p-4 mb-4 flex flex-wrap gap-6 text-xs">
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "linear-gradient(135deg,#059669,#047857)" }} /> Subvaluado + dueño directo</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "linear-gradient(135deg,#10b981,#059669)" }} /> Subvaluado</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "linear-gradient(135deg,#f59e0b,#d97706)" }} /> Interesante</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "linear-gradient(135deg,#ef4444,#dc2626)" }} /> Precio sospechoso</span>
        <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-full" style={{ background: "linear-gradient(135deg,#64748b,#475569)" }} /> Normal</span>
        <span className="flex items-center gap-2 ml-auto text-slate-500">
          <span className="inline-block w-3 h-3 rounded-full bg-emerald-600 animate-pulse ring-2 ring-emerald-300" />
          Top del día (pulse)
        </span>
      </div>

      {/* Segment chips — shared with tablero */}
      <div className="mb-4">
        <ChipBar current={chip.id} counts={counts} onChange={setChip} />
      </div>

      {/* Buscar oportunidades — dispara Apify */}
      {zonaSel && (
        <div className="bg-white border border-slate-200 mb-4" data-testid="radar-search-panel">
          <div className="p-4 flex flex-wrap items-center justify-between gap-4">
            <div className="flex-1 min-w-[240px]">
              <p className="font-heading text-sm font-bold text-slate-900">
                Buscar oportunidades en <span className="text-emerald-800">{zonaSel.nombre}</span>
              </p>
              <p className="text-[11px] text-slate-500 mt-1">
                Trae los últimos avisos disponibles en la zona
              </p>
            </div>
            <Button
              onClick={() => setShowCostPreview(true)}
              disabled={searching}
              data-testid="radar-search-btn"
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none px-5 h-10 min-w-[220px]"
            >
              {searching ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Buscando oportunidades…</>
              ) : (
                <><Search className="w-4 h-4 mr-2" /> Buscar oportunidades en esta zona</>
              )}
            </Button>
          </div>

          {searching && (
            <div className="border-t border-slate-200 bg-emerald-50 px-4 py-2.5 flex items-center gap-2 text-xs text-emerald-900"
                 data-testid="radar-search-progress">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              <span className="flex-1">
                {searchProgress?.message || "Actualizando avisos de la zona…"}
                {searchProgress?.total > 0 && searchProgress?.current > 0 && (
                  <span className="text-emerald-800/80 ml-1">
                    ({searchProgress.current}/{searchProgress.total})
                  </span>
                )}
              </span>
              {activeCapturaId && (
                <button
                  type="button"
                  onClick={cancelSearch}
                  disabled={cancelling}
                  data-testid="radar-cancel-search"
                  className="flex items-center gap-1.5 px-2.5 py-1 border border-red-300 bg-white text-red-700 font-heading font-bold uppercase text-[10px] tracking-[0.06em] hover:bg-red-50 hover:border-red-500 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                  title="Aborta el/los run(s) de Apify y registra el costo hasta el abort"
                >
                  {cancelling ? (
                    <><Loader2 className="w-3 h-3 animate-spin" /> Cancelando…</>
                  ) : (
                    <><X className="w-3 h-3" /> Cancelar búsqueda</>
                  )}
                </button>
              )}
            </div>
          )}

          {searchError && !searching && (
            <div className="border-t border-red-200 bg-red-50 px-4 py-2.5 flex items-start gap-2 text-xs text-red-800"
                 data-testid="radar-search-error">
              <AlertCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">La búsqueda falló</div>
                <div className="text-red-700 mt-0.5">{searchError}</div>
              </div>
              <button onClick={() => setSearchError(null)} className="text-red-600 hover:text-red-800" aria-label="Cerrar">
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}

          {searchSummary && !searching && !searchError && (
            <div className="border-t border-emerald-200 bg-emerald-50 px-4 py-3" data-testid="radar-search-summary">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-2">
                  <Sparkles className="w-4 h-4 text-emerald-700 mt-0.5" />
                  <div className="text-xs text-emerald-900">
                    <div className="font-semibold">
                      {searchSummary.nuevas} oportunidades nuevas ·{" "}
                      {searchSummary.subvaluadas} subvaluadas ·{" "}
                      {searchSummary.dueno_directo} dueño directo
                    </div>
                    <div className="text-emerald-800/80 mt-1">
                      {searchSummary.actualizadas > 0 && `${searchSummary.actualizadas} actualizadas · `}
                      {searchSummary.filtradas_zona} avisos dentro del polígono
                      {" · "}en {searchSummary.duracion_seg}s
                    </div>
                  </div>
                </div>
                <button onClick={() => setSearchSummary(null)} className="text-emerald-700 hover:text-emerald-900" aria-label="Cerrar resumen">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="bg-white border border-slate-200 flex" data-testid="radar-map-wrapper">
        <div style={{ height: 620 }} className="flex-1 min-w-0">
          {loading ? (
            <div className="h-full flex items-center justify-center text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Cargando mapa...
            </div>
          ) : (
            <MapContainer center={center} zoom={14} scrollWheelZoom={true} style={{ height: "100%", width: "100%" }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
                url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                subdomains="abcd"
                maxZoom={20}
              />
              <RecenterOnZone center={center} />
              <FlyToOpp opp={selectedOppLite} panelOpen={!!selectedOppId} />
              {zonaPolygon && (
                <Polygon positions={zonaPolygon} pathOptions={{ color: "#0f766e", weight: 2.5, fillOpacity: 0.06, fillColor: "#10b981" }} />
              )}
              {previewLeaflet && (
                <Polygon positions={previewLeaflet} pathOptions={{ color: "#059669", weight: 3, fillOpacity: 0.15, dashArray: "6 4" }} />
              )}
              {previewPin && (
                <Marker
                  position={[previewPin.lat, previewPin.lng]}
                  draggable={!!previewPin.draggable}
                  eventHandlers={{
                    dragend: (e) => {
                      const p = e.target.getLatLng();
                      previewPin.onDrag?.(p.lat, p.lng);
                    },
                  }}
                />
              )}
              <MarkerClusterGroup markers={clusterMarkers} />
              {heroMarkers.map((h) => (
                <Marker
                  key={h.o.opportunity_id}
                  position={h.position}
                  icon={h.icon}
                  eventHandlers={{ click: () => openAviso(h.o.opportunity_id) }}
                />
              ))}
            </MapContainer>
          )}
        </div>

        {/* Panel lateral de aviso — reutiliza RadarFicha en modo embedded.
            Ancho 440px = mismo que el drawer de Tierra (unificación P-2). */}
        {selectedOppId && (
          <aside
            data-testid="aviso-ficha"
            style={{ height: 620 }}
            className="w-[440px] shrink-0 border-l border-slate-300 bg-white overflow-y-auto"
          >
            <RadarFicha embeddedId={selectedOppId} onClose={closeAviso} />
          </aside>
        )}
      </div>

      {/* Dialog de nueva zona */}
      <ZonaDialog
        open={openNewZona}
        onClose={() => setOpenNewZona(false)}
        context="propiedades"
        onSaved={Object.assign(
          (z) => { setZonaSel(z); load(); },
          { _preview: setPreviewPolygon, _pin: setPreviewPin }
        )}
      />

      {/* Dialog de edición (reusa ZonaDialog) */}
      <ZonaDialog
        open={!!editingZona}
        zona={editingZona}
        onClose={() => setEditingZona(null)}
        context="propiedades"
        onSaved={Object.assign(
          (z) => { setZonaSel(z); load(); },
          { _preview: setPreviewPolygon, _pin: setPreviewPin }
        )}
      />

      {/* Confirmación de borrado */}
      <AlertDialog open={!!deletingZona} onOpenChange={(v) => !v && !deleting && setDeletingZona(null)}>
        <AlertDialogContent className="rounded-none z-[1300]" data-testid="delete-zona-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              ¿Borrar la zona {deletingZona?.nombre ? `«${deletingZona.nombre}»` : ""}?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-xs text-slate-600">
              Se elimina solo la definición de la búsqueda. Las oportunidades ya capturadas
              siguen disponibles en el tablero con su ubicación y barrio.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting} className="rounded-none border-slate-300">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-zona-confirm"
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-700 hover:bg-red-800 text-white rounded-none"
            >
              {deleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Borrar zona
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Preview de costo antes de disparar Apify — techo honesto, no promesa.
          El botón de cancelar durante la corrida sigue cortando el gasto. */}
      <AlertDialog open={showCostPreview} onOpenChange={(v) => !v && setShowCostPreview(false)}>
        <AlertDialogContent className="rounded-none z-[1300]" data-testid="cost-preview-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">
              Buscar en «{zonaSel?.nombre}» — ¿confirmás?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-xs text-slate-700 space-y-2 pt-1">
                <p>
                  Esta búsqueda va a consultar los portales de propiedades. Cuesta{" "}
                  <strong>hasta ~US$ {costPreview.total.toFixed(2)}</strong> según cuántos avisos
                  encuentre. Es un tope, no un número prometido — el real puede ser mucho menor.
                </p>
                <ul className="border-l-2 border-slate-300 pl-3 space-y-0.5 text-[11px] font-mono">
                  {costPreview.runs.map((r) => (
                    <li key={r.name} data-testid={`cost-preview-run-${r.name.toLowerCase()}`}>
                      {r.name}: hasta {r.maxItems} avisos × US$ {r.perItem} = US$ {(r.maxItems * r.perItem).toFixed(2)}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-500">
                  Podés cancelar en cualquier momento durante la corrida. El actor cobra
                  solo por los avisos que ya devolvió hasta el corte.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none border-slate-300">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="cost-preview-confirm"
              onClick={() => { setShowCostPreview(false); runSearch(); }}
              className="bg-emerald-700 hover:bg-emerald-800 text-white rounded-none"
            >
              Buscar (hasta ~US$ {costPreview.total.toFixed(2)})
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
